import { alpha, Grid, GridSize, TextField, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux';
import { IRootState } from '../../store/reducer';
import { Operators, PickemParlayType } from '../pick6/store/types';
import { getPickemCalculatedData, legCountAndPickemTypeToPayoutsDictionary } from '../pick6/optimizerHelper';
import { formatToUSDollarFloat, IsOperatorPeer2Peer } from './store/types';
import { APIConstants } from '../utils/api/constants';
import { Lock } from '@mui/icons-material';

const WagerPayout = () => {
  const theme = useTheme();
  const [wager, setWager] = React.useState('$100.00');
  const {
    selectedLegs,
    operator,
    pickemParlayType,
  } = useSelector((state: IRootState) => state.pickemOptimizer);
  const { authenticated } = useSelector((state: IRootState) => state.common);
  const isDisplayUnlocked = authenticated.status === APIConstants.SUCCEEDED && authenticated.data.result;

  const getColor = (value: number) => {
    if (value) {
      switch (true) {
        case value > 0.05:
          return { color: theme.palette.success.main };
        case value > 0:
          return { color: alpha(theme.palette.primary.main, 0.6) };
        case value <= 0:
          return { color: theme.palette.error.main };
        default:
          return '';
      }
    }
    return '';
  };

  const changeWager = (e: React.ChangeEvent<HTMLInputElement>) => {
    const w = e.target.value
      .replace('-', '')
      .replace('$', '')
      .replace(/,/gi, '');

    if (Number(w) > 10000) return null;

    if (Number.isNaN(parseFloat(w))) {
      return setWager(`$${0}`);
    }

    const decimals = w.split('.');
    if (decimals && w.indexOf('.') > -1) {
      return setWager(
        `$${parseInt(decimals[0], 10)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimals[1].slice(0, 2)}`,
      );
    }

    return setWager(
      `$${parseInt(w, 10)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    );
  };

  const calculatePayout = (oddsAmerican: string): number => {
    if (!oddsAmerican) {
      return 0;
    }
    const wagerAmount: number = Number(
      wager.replace('$', '').replace(/,/gi, ''),
    );
    let winnings: number = wagerAmount + (wagerAmount * Number(oddsAmerican)) / 100;
    if (Number(oddsAmerican) < 0) {
      // Negative Odds
      winnings = wagerAmount + (wagerAmount / (Number(oddsAmerican) / 100)) * -1;
    }

    if (Number.isNaN(winnings)) {
      return 0;
    }
    return winnings;
  };

  const outcomes = (selectedLegs && selectedLegs.length >= 2 && selectedLegs) || [];
  const displayData: {
    oddsAmerican: string;
    expectedWinPct: number;
    expectedRoiPct: number;
  } = getPickemCalculatedData(outcomes, operator ?? Operators.PRIZEPICKS, pickemParlayType ?? PickemParlayType.FLEX);

  const PickemPayoutComponent = () => {
    let payouts = legCountAndPickemTypeToPayoutsDictionary[`${operator}|${pickemParlayType}|${outcomes.length}`];
    if (payouts === undefined) {
      payouts = {
        5: -1,
        4: -1,
      };
    }
    const payoutsKeys = Object.keys(payouts).reverse();
    if (payoutsKeys.length === 1) {
      return (
        <Grid sx={{
          borderTopLeftRadius: 10,
          display: 'flex',
          borderTopRightRadius: 10,
          padding: '15px 20px',
          border: '1px solid rgba(0, 174, 7, 0.50)',
          background: 'rgba(54, 141, 57, 0.15)',
          width: '100%',
          [theme.breakpoints.down('xs')]: {
            paddingTop: '6px',
            paddingBottom: '6px',
          },
        }}>
          <Grid item xs={12} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="body1" sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              [theme.breakpoints.down('sm')]: {
                fontSize: '1.125rem',
              },
              color: theme.palette.success.main,
            }}>
              Winning all Legs pays {Object.values(payouts)[0]}x your stake.
            </Typography>
          </Grid>
        </Grid>
      );
    }
    const xs = Math.round(12 / payoutsKeys.length) as GridSize;
    return (
      <Grid sx={{
        borderTopLeftRadius: 10,
        display: 'flex',
        borderTopRightRadius: 10,
        padding: '15px 20px',
        border: `2px solid ${'#252525'}`,
        background: '#101010',
        width: '100%',
        [theme.breakpoints.down('xs')]: {
          paddingTop: '6px',
          paddingBottom: '6px',
        },
      }}>
        {payoutsKeys.map((key: string) => (
          <Grid item xs={xs} key={key} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="body1" sx={{
              fontSize: '18px',
              fontWeight: 500,
              color: alpha(theme.palette.common.white, 0.6),
              [theme.breakpoints.down('sm')]: {
                fontSize: '12px',
              },
            }}>
              {key} Right Payout
            </Typography>
            <Typography sx={{
              fontSize: '18px',
              fontWeight: 600,
              [theme.breakpoints.down('sm')]: {
                fontSize: '16px',
              },
              color: theme.palette.common.white,
            }} variant="body1">
              {payouts[Number(key)] === -1 ? '-' : `${payouts[Number(key)].toString()}x`}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  //Let our users know peer 2 peer pickems are based on avergae payout. 
  const PickemPeerNoticeComponent = () => {
    if (IsOperatorPeer2Peer(operator)) {
      return (
        <Grid sx={{
          display: 'flex',
          padding: '15px 20px',
          border: '1px solid rgba(0, 174, 7, 0.50)',
          background: 'rgba(54, 141, 57, 0.15)',
          width: '100%',
          [theme.breakpoints.down('xs')]: {
            paddingTop: '6px',
            paddingBottom: '6px',
          },
        }}>
          <Grid item xs={12} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="body1" sx={{
              fontSize: '10px',
              textAlign: 'center',
              fontWeight: 600,
              color: theme.palette.success.main,
            }}>
              EV, odds and payouts are based on estimated average payouts.
              Actual prizes awarded are based on contest results.
            </Typography>
          </Grid>
        </Grid>
      );
    } return null;
  };

  const PickemPowerPlayPayoutItem = () => {
    const payoutDictionary = legCountAndPickemTypeToPayoutsDictionary[`${operator}|${PickemParlayType.POWER}|${outcomes.length}`];
    let payout = '-';
    payout = `${Object.values(payoutDictionary)[0]}x`;
    return (
      <Grid item xs={4} className="row">
        <Typography variant="body1" className="label">
          {operator !== Operators.NONE ? 'Entry' : 'Parlay'} Payout
        </Typography>
        <Typography variant="body1" sx={{
          fontSize: '1.25rem',
          fontWeight: 600,
          [theme.breakpoints.down('sm')]: {
            fontSize: '1.125rem',
          },
          color: theme.palette.success.main,
        }}>
          {payout}
        </Typography>
      </Grid>
    );
  };

  return (
    <Grid spacing={2} mt={2} sx={{ marginLeft: '0', width: '100%', }}>
      <Grid item xs={12}>
        {operator === Operators.NONE && (
          <Grid sx={{
            borderTopLeftRadius: 10,
            display: 'flex',
            borderTopRightRadius: 10,
            padding: '15px 20px',
            border: `2px solid #252525`,
            background: '#101010',
            width: '100%',
            [theme.breakpoints.down('xs')]: {
              paddingTop: '6px',
              paddingBottom: '6px',
            },
          }}>
            <Grid xs={6} sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              [theme.breakpoints.down('xs')]: {
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              },
              '& .label': {
                marginRight: '24px',
                fontSize: '18px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '10px',
                  marginRight: '8px',
                },
                [theme.breakpoints.down('xs')]: {
                  marginBottom: '8px',
                },
              },
            }}>
              <Typography variant="body1" className="label">
                Wager:
              </Typography>
              <TextField
                size="small"
                id="wager-input2"
                variant="outlined"
                value={wager}
                onChange={changeWager}
                sx={{
                  backgroundColor: 'rgba(28, 28, 28, 0.87)',
                  border: '0.5px solid rgba(255, 255, 255, 0.40)',
                  borderRadius: '4px',
                  '& input': {
                    color: alpha(theme.palette.common.white, 0.6)
                  }
                }}
              />
            </Grid>
            <Grid xs={6} sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              [theme.breakpoints.down('xs')]: {
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-end',
              },
              '& .label': {
                marginRight: '4px',
                fontSize: '18px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '10px',
                },
                [theme.breakpoints.down('xs')]: {
                  marginBottom: '12px',
                  marginRight: '0',
                },
              },
              '& .value': {
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'success.main',
                [theme.breakpoints.down('md')]: {
                  fontSize: '16px',
                },
              },
            }}>
              <Typography variant="body1" className="label">
                Payout
              </Typography>
              <Typography variant="body1" className="value">
                {formatToUSDollarFloat(calculatePayout(displayData.oddsAmerican))}
              </Typography>
            </Grid>
          </Grid>
        )}
        {(operator !== Operators.NONE && pickemParlayType === PickemParlayType.FLEX) && (
          <>
            <PickemPayoutComponent />
            <PickemPeerNoticeComponent />
          </>
        )}
        <Grid sx={{
          marginBottom: '8px',

          '& .logo-wrapper': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            '& .logo': {
              width: 160,
              height: 160,
              [theme.breakpoints.down('md')]: {
                width: 120,
                height: 120,
              },
              [theme.breakpoints.down('xs')]: {
                width: 80,
                height: 80,
              },
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              },
            },
          },
          '& .data-wrapper': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            '& .container': {
              display: 'flex',
              flexDirection: 'row',
              background: '#252525',
              border: `1px solid ${'#252525'}`,
              borderBottomLeftRadius: 10,
              borderBottomRightRadius: 10,
              padding: '15px 20px',
              '& .row': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                '& .label': {
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  color: 'rgba(210,210,210,0.7)',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '10px',
                  },
                },
                '& .value': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '1.125rem',
                  },
                  '&.primary': {
                    color: theme.palette.common.white,
                  },
                  '&.success': {
                    color: theme.palette.success.main,
                  },
                },
              },
            },
          },
        }}>
          <Grid xs={12} className="data-wrapper mobile">
            <Grid mt={0} className="container" sx={{
              width: '100%',
            }}>
              {(operator === Operators.NONE) && (
                <Grid xs={4} className="row">
                  <Typography variant="body1" className="label">
                    Parlay Odds
                  </Typography>
                  <Typography variant="body1" className="value primary">
                    {displayData?.oddsAmerican ? (
                      displayData.oddsAmerican
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
              )}
              {(operator !== Operators.NONE && pickemParlayType === PickemParlayType.POWER) && (
                <PickemPowerPlayPayoutItem />
              )}
              <Grid xs={(operator === Operators.NONE || pickemParlayType !== PickemParlayType.FLEX) ? 4 : 6} className="row">
                <Typography variant="body1" className="label">
                  xEV
                </Typography>
                <Typography
                  variant="body1"
                  className='value'
                  sx={{ ...getColor(displayData?.expectedRoiPct) }}
                >
                  {isDisplayUnlocked ? (
                    Number.isFinite(displayData?.expectedRoiPct) ? (
                      `${Math.floor(displayData?.expectedRoiPct)}%`
                    ) : (
                      '-'
                    )
                  ) : (
                    <Lock />
                  )}
                </Typography>
              </Grid>
              <Grid xs={(operator === Operators.NONE || pickemParlayType !== PickemParlayType.FLEX) ? 4 : 6} className="row">
                <Typography variant="body1" className="label">
                  xWin
                </Typography>
                <Typography
                  variant="body1"
                  className={`value ${getColor(displayData?.expectedWinPct)}`}
                  sx={{
                    color: 'white'
                  }}
                >
                  {/* {isDisplayUnlocked ? (
                    Number.isFinite(displayData?.expectedWinPct) ? (
                      `${Math.floor(displayData?.expectedWinPct) <= 0
                        ? '<1'
                        : Math.floor(displayData?.expectedWinPct)
                      }%`
                    ) : (
                      '-'
                    )
                  ) : (<>-</>)} */}
                  {
                    Number.isFinite(displayData?.expectedWinPct) ? (
                      `${Math.floor(displayData?.expectedWinPct) <= 0
                        ? '<1'
                        : Math.floor(displayData?.expectedWinPct)
                      }%`
                    ) : (
                      '-'
                    )
                  }
                </Typography>
              </Grid>
              {/* SGP Correlation Disclaimer */}
              {selectedLegs && selectedLegs.length >= 2 && (
                <Grid xs={12} sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: '12px',
                  width: '100%',
                }}>
                  <Typography variant="body2" sx={{
                    fontSize: '11px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    color: alpha(theme.palette.common.white, 0.6),
                  }}>
                    Note: xWin and xEV do not account for SGP correlation in odds or true win probability.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default WagerPayout